import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react'
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material'
import {
  Close,
  Description,
  DirectionsCar,
  History,
  LocalShipping,
  Person,
  Search,
  Terminal,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { inventoryV3Href } from '@/views/SuperAdmin/InventoryV3/inventoryV3Route'
import { leadDeepLinkPath } from '@/views/SuperAdmin/Management/LeadManagement/util/leadDeepLink'
import { useAuth } from 'hooks/auth/useAuth'
import { parseSlashQuery } from 'hooks/search/parseSlashQuery'
import {
  filterSlashCommands,
  visibleSlashCommands,
  type SlashCommandDef,
} from 'hooks/search/slashCommands'
import { filterPages, usePageSearchProvider } from 'hooks/search/usePageSearchProvider'
import { useEntitySearch } from 'hooks/search/useEntitySearch'
import { useCommandPaletteHotkeys } from 'hooks/search/useCommandPaletteHotkeys'
import { readPaletteRecents, writePaletteRecent } from 'hooks/search/searchRecents'
import { highlightPrefix } from 'hooks/search/filterSearchItems'
import { PALETTE_GROUP_LABEL, type PaletteGroup, type PaletteItem } from 'hooks/search/paletteTypes'
import {
  getClientUserDisplayName,
  type ClientUserSearchResult,
} from 'hooks/admin/useClientUserSearch'
import type { InvoiceSearchResult } from 'hooks/admin/useInvoiceSearch'
import type { LeadSearchResult } from 'hooks/admin/useLeadSearch'
import type { TransporterPackageSearchResult } from 'hooks/admin/useTransporterPackageSearch'
import type { Vehicle } from '@otw/models'
import { getDocsHomeUrl } from '@/constants/docsUrl'
import { ASSISTANT_FEATURE_FLAG_ID } from 'constants/featureFlags'
import { useFeatureFlag } from 'hooks/useFeatureFlag'
import { useAssistant } from 'components/assistant/AssistantContext'
import { CommandPaletteTrigger } from './CommandPaletteTrigger'
import { CommandPaletteOnboarding } from './CommandPaletteOnboarding'

interface CommandPaletteProps {
  isMobile: boolean
  isOwner: boolean
  isClientContext: boolean
  isTimerActive: boolean
  onClockIn: () => void
  onClockOut: () => void
  onDemoVisibilityChange?: (visible: boolean) => void
}

const GROUP_ORDER: PaletteGroup[] = [
  'recents',
  'commands',
  'pages',
  'inventory',
  'leads',
  'invoices',
  'clients',
  'transport',
]

const HighlightText = ({ text, query }: { text: string; query: string }) => {
  const parts = highlightPrefix(text, query)
  if (!parts) return <>{text}</>
  return (
    <>
      {parts.before}
      <Box component='span' sx={{ fontWeight: 700 }}>
        {parts.match}
      </Box>
      {parts.after}
    </>
  )
}

const groupIcon = (group: PaletteGroup) => {
  if (group === 'inventory') return <DirectionsCar fontSize='small' />
  if (group === 'leads') return <Person fontSize='small' />
  if (group === 'invoices') return <Description fontSize='small' />
  if (group === 'clients') return <Person fontSize='small' />
  if (group === 'transport') return <LocalShipping fontSize='small' />
  if (group === 'recents') return <History fontSize='small' />
  if (group === 'commands') return <Terminal fontSize='small' />
  return <Search fontSize='small' />
}

const flatHasAskFallback = (items: PaletteItem[], query: string) =>
  items.some(item => item.id === `ask:${query.trim()}`)


const commandToItem = (command: SlashCommandDef, docsUrl: string): PaletteItem => {
  const slash = command.slashes[0]
  if (command.kind === 'scope' && command.scope) {
    return {
      id: command.id,
      group: 'commands',
      title: command.title,
      subtitle: command.description,
      slash,
      action: { kind: 'scope', insert: `${slash} ` },
    }
  }
  if (command.action === 'clock-in') {
    return {
      id: command.id,
      group: 'commands',
      title: command.title,
      subtitle: command.description,
      slash,
      action: { kind: 'clock-in' },
    }
  }
  if (command.action === 'clock-out') {
    return {
      id: command.id,
      group: 'commands',
      title: command.title,
      subtitle: command.description,
      slash,
      action: { kind: 'clock-out' },
    }
  }
  if (command.action === 'ask') {
    return {
      id: command.id,
      group: 'commands',
      title: command.title,
      subtitle: command.description,
      slash,
      action: { kind: 'ask' },
    }
  }
  if (command.action === 'new-lead') {
    return {
      id: command.id,
      group: 'commands',
      title: command.title,
      subtitle: command.description,
      slash,
      action: { kind: 'new-lead' },
    }
  }
  if (command.action === 'decode-vin') {
    return {
      id: command.id,
      group: 'commands',
      title: command.title,
      subtitle: command.description,
      slash,
      action: { kind: 'decode-vin' },
    }
  }
  if (command.action === 'price') {
    return {
      id: command.id,
      group: 'commands',
      title: command.title,
      subtitle: command.description,
      slash,
      action: { kind: 'price' },
    }
  }
  if (command.id === 'goto-docs' || command.href) {
    return {
      id: command.id,
      group: 'commands',
      title: command.title,
      subtitle: command.description,
      slash,
      action: { kind: 'external', href: command.href || docsUrl },
    }
  }
  return {
    id: command.id,
    group: 'commands',
    title: command.title,
    subtitle: command.description,
    slash,
    action: { kind: 'navigate', path: command.path || '/' },
  }
}

const vehicleTitle = (vehicle?: Vehicle): string => {
  const stock = vehicle?.stock_number?.trim()
  const ymm = [vehicle?.year, vehicle?.make, vehicle?.model].filter(Boolean).join(' ')
  return [stock, ymm].filter(Boolean).join(' · ') || vehicle?.vin || 'Vehicle'
}

export function CommandPalette({
  isMobile,
  isOwner,
  isClientContext,
  isTimerActive,
  onClockIn,
  onClockOut,
  onDemoVisibilityChange,
}: CommandPaletteProps) {
  const navigate = useNavigate()
  const { userInfo } = useAuth()
  const { pages } = usePageSearchProvider()
  const assistantEnabled = useFeatureFlag(ASSISTANT_FEATURE_FLAG_ID)
  const assistant = useAssistant()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [recents, setRecents] = useState(readPaletteRecents)
  const inputRef = useRef<HTMLInputElement>(null)
  const [triggerNode, setTriggerNode] = useState<HTMLDivElement | null>(null)
  const [dialogFieldNode, setDialogFieldNode] = useState<HTMLDivElement | null>(null)
  const [demoActive, setDemoActive] = useState(false)
  const demoAnchor = open && dialogFieldNode ? dialogFieldNode : triggerNode

  const parsed = useMemo(() => parseSlashQuery(query), [query])
  const isAdmin = userInfo?.role === 'admin' || userInfo?.role === 'babyAdmin'
  const docsUrl = getDocsHomeUrl()

  const visibility = {
    role: userInfo?.role,
    isOwner,
    isClientContext,
    isMobile,
    isTimerActive,
    assistantEnabled,
  }

  const commands = useMemo(
    () => visibleSlashCommands(visibility, pages),
    [
      userInfo?.role,
      isOwner,
      isClientContext,
      isMobile,
      isTimerActive,
      assistantEnabled,
      pages,
    ],
  )

  const entity = useEntitySearch({
    open,
    enabled: isAdmin,
    scope: parsed.scope,
    query: parsed.query,
  })

  const items = useMemo((): PaletteItem[] => {
    const next: PaletteItem[] = []
    const commandFilter = parsed.commandsOnly
      ? parsed.token || parsed.query
      : parsed.isSlash
        ? ''
        : parsed.query

    const showCommands = !parsed.scope || parsed.scope === 'goto' || parsed.commandsOnly
    if (showCommands) {
      const filtered = parsed.scope === 'goto'
        ? filterSlashCommands(commands.filter((cmd) => cmd.kind === 'goto'), parsed.query)
        : filterSlashCommands(commands, commandFilter)
      filtered.forEach((command) => next.push(commandToItem(command, docsUrl)))
    } else if (parsed.scope && !parsed.query) {
      const goTo = commands.find(
        (command) => command.kind === 'goto' && command.scope === parsed.scope,
      )
      const fallbackGoTo = commands.find((command) => {
        if (command.kind !== 'goto') return false
        if (parsed.scope === 'inventory') return command.path === '/admin/inventory-v3'
        if (parsed.scope === 'lead') return command.path === '/admin/lead-management'
        if (parsed.scope === 'invoice') return command.path === '/admin/invoice-management'
        if (parsed.scope === 'transport') {
          return command.path === '/admin/management/transporter-management'
        }
        return false
      })
      const match = goTo || fallbackGoTo
      if (match) next.push(commandToItem(match, docsUrl))
    }

    const showPages =
      parsed.scope === 'goto' ||
      parsed.scope === null ||
      parsed.commandsOnly
    if (showPages) {
      const pageQuery = parsed.scope === 'goto' ? parsed.query : parsed.commandsOnly ? parsed.token : parsed.query
      filterPages(pages, pageQuery).forEach((page) => {
        next.push({
          id: `page:${page.id}`,
          group: 'pages',
          title: page.title,
          subtitle: page.path || page.href,
          action: page.href
            ? { kind: 'external', href: page.href }
            : { kind: 'navigate', path: page.path || '/' },
        })
      })
    }

    if (!parsed.commandsOnly && isAdmin) {
      if (!parsed.scope || parsed.scope === 'inventory') {
        entity.inventory.forEach((vehicle) => {
          next.push({
            id: `inventory:${vehicle.id}`,
            group: 'inventory',
            title: vehicleTitle(vehicle),
            subtitle: vehicle.vin ? `VIN: ${vehicle.vin}` : undefined,
            chip: 'Inventory',
            action: { kind: 'navigate', path: inventoryV3Href({ open: 'edit', truckId: vehicle.id }) },
          })
        })
      }
      if (!parsed.scope || parsed.scope === 'lead') {
        entity.leadResults.forEach((result: LeadSearchResult) => {
          const leadNumber = result.lead.leadNumber?.trim()
          next.push({
            id: `lead:${result.lead.id}:${result.vehicle?.id || 'none'}`,
            group: 'leads',
            title: leadNumber || result.lead.customerName || vehicleTitle(result.vehicle),
            subtitle: [result.lead.customerName, result.vehicle?.vin && `VIN: ${result.vehicle.vin}`]
              .filter(Boolean)
              .join(' · '),
            chip: 'Lead',
            chipColor: 'secondary',
            action: { kind: 'navigate', path: leadDeepLinkPath(result.lead.id) },
          })
        })
      }
      if (!parsed.scope || parsed.scope === 'invoice') {
        entity.invoiceResults.forEach((result: InvoiceSearchResult) => {
          const inv = result.invoice
          if (result.leadId) {
            next.push({
              id: `invoice-lead:${inv.id}`,
              group: 'invoices',
              title: inv.invoice_number || 'Invoice',
              subtitle: result.leadName || inv.client_name,
              chip: 'Lead',
              chipColor: 'secondary',
              action: { kind: 'navigate', path: leadDeepLinkPath(result.leadId) },
            })
          }
          next.push({
            id: `invoice:${inv.id}`,
            group: 'invoices',
            title: inv.invoice_number || 'Invoice',
            subtitle: inv.lead_name || inv.client_name,
            chip: 'Invoice',
            action: {
              kind: 'navigate',
              path: `/admin/invoice-management?tab=1&highlightInvoiceId=${encodeURIComponent(inv.id)}`,
            },
          })
        })
      }
      if (!parsed.scope || parsed.scope === 'client') {
        entity.clientResults.forEach((result: ClientUserSearchResult) => {
          const name = getClientUserDisplayName(result.client)
          if (result.type === 'client_shipment_plan') {
            const params = new URLSearchParams()
            params.set('transportTruckId', result.transportTruckId)
            params.set('shipmentPlanId', result.plan.id)
            if (result.transportVin?.trim()) params.set('transportVin', result.transportVin.trim())
            next.push({
              id: `client-plan:${result.plan.id}:${result.client.id}`,
              group: 'clients',
              title: name,
              subtitle: ['Shipment plan', result.vehicleLabel, result.plan.description?.trim()]
                .filter(Boolean)
                .join(' · '),
              chip: 'Transport',
              chipColor: 'info',
              action: {
                kind: 'navigate',
                path: `/admin/management/transporter-management?${params.toString()}`,
              },
            })
            return
          }
          next.push({
            id: `client-lead:${result.lead.id}`,
            group: 'clients',
            title: name,
            subtitle: [result.lead.leadNumber?.trim(), result.lead.customerName, result.lead.email]
              .filter(Boolean)
              .join(' · '),
            chip: 'Lead',
            chipColor: 'secondary',
            action: { kind: 'navigate', path: leadDeepLinkPath(result.lead.id) },
          })
        })
      }
      if (!parsed.scope || parsed.scope === 'transport') {
        entity.transportResults.forEach((result: TransporterPackageSearchResult) => {
          next.push({
            id: `transport:${result.package.id}:${result.vehicle.id}`,
            group: 'transport',
            title: vehicleTitle(result.vehicle),
            subtitle: result.package.name || 'Package',
            chip: 'Transporter',
            chipColor: 'info',
            action: {
              kind: 'navigate',
              path: `/admin/management/transporter-management#package-${result.package.id}`,
            },
          })
        })
      }
    }

    if (!query.trim()) {
      recents.forEach((recent) => {
        next.unshift({
          id: `recent:${recent.id}`,
          group: 'recents',
          title: recent.label,
          subtitle: recent.path,
          action: { kind: 'navigate', path: recent.path },
        })
      })
    }

    if (
      assistantEnabled &&
      userInfo?.role === 'admin' &&
      query.trim() &&
      !parsed.commandsOnly &&
      !parsed.scope &&
      !flatHasAskFallback(next, query)
    ) {
      next.push({
        id: `ask:${query.trim()}`,
        group: 'commands',
        title: `Ask assistant: ${query.trim()}`,
        subtitle: 'Send this to the portal assistant',
        slash: '/ask',
        action: { kind: 'ask', seed: query.trim() },
      })
    }

    return next
  }, [
    assistantEnabled,
    commands,
    docsUrl,
    entity.clientResults,
    entity.inventory,
    entity.invoiceResults,
    entity.leadResults,
    entity.transportResults,
    isAdmin,
    pages,
    parsed,
    query,
    recents,
    userInfo?.role,
  ])

  const grouped = useMemo(() => {
    return GROUP_ORDER.map((group) => ({
      group,
      items: items.filter((item) => item.group === group),
    })).filter((section) => section.items.length > 0)
  }, [items])

  const flatItems = useMemo(() => grouped.flatMap((section) => section.items), [grouped])

  useEffect(() => {
    setSelectedIndex(0)
  }, [query, open])

  useEffect(() => {
    if (selectedIndex >= flatItems.length) {
      setSelectedIndex(Math.max(0, flatItems.length - 1))
    }
  }, [flatItems.length, selectedIndex])

  const close = useCallback(() => {
    setOpen(false)
    setQuery('')
    window.requestAnimationFrame(() => {
      const active = document.activeElement
      if (active instanceof HTMLElement) active.blur()
    })
  }, [])

  const openPalette = useCallback((seed = '') => {
    setQuery(seed)
    setOpen(true)
  }, [])

  const setPaletteQuery = useCallback((value: string) => {
    setQuery(value)
  }, [])

  const startDemoPalette = useCallback((seed?: string) => {
    setDemoActive(true)
    openPalette(seed)
  }, [openPalette])

  const endDemoPalette = useCallback(() => {
    setDemoActive(false)
    close()
  }, [close])

  const togglePalette = useCallback(() => {
    setOpen((current) => {
      if (current) {
        setQuery('')
        return false
      }
      return true
    })
  }, [])

  useCommandPaletteHotkeys({
    open,
    onOpen: openPalette,
    onToggle: togglePalette,
  })

  useEffect(() => {
    if (open) {
      const id = window.setTimeout(() => inputRef.current?.focus(), 0)
      return () => window.clearTimeout(id)
    }
    return undefined
  }, [open])

  const runItem = useCallback(
    (item: PaletteItem) => {
      if (item.action.kind === 'scope') {
        setQuery(item.action.insert)
        return
      }
      if (item.action.kind === 'clock-in') {
        onClockIn()
        close()
        return
      }
      if (item.action.kind === 'clock-out') {
        onClockOut()
        close()
        return
      }
      if (item.action.kind === 'ask') {
        assistant?.open(item.action.seed)
        close()
        return
      }
      if (item.action.kind === 'new-lead') {
        navigate('/admin/lead-management')
        close()
        return
      }
      if (item.action.kind === 'decode-vin') {
        navigate('/admin/tools/vin-decoder')
        close()
        return
      }
      if (item.action.kind === 'price') {
        navigate('/optic-gray-book-calculator')
        close()
        return
      }
      if (item.action.kind === 'external') {
        window.open(item.action.href, '_blank', 'noopener,noreferrer')
        writePaletteRecent({
          type: item.group,
          id: item.id,
          path: item.action.href,
          label: item.title,
        })
        setRecents(readPaletteRecents())
        close()
        return
      }
      writePaletteRecent({
        type: item.group,
        id: item.id,
        path: item.action.path,
        label: item.title,
      })
      setRecents(readPaletteRecents())
      navigate(item.action.path)
      close()
    },
    [assistant, close, navigate, onClockIn, onClockOut],
  )

  const onInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' || (event.ctrlKey && event.key.toLowerCase() === 'n')) {
      event.preventDefault()
      setSelectedIndex((index) => Math.min(index + 1, Math.max(0, flatItems.length - 1)))
      return
    }
    if (event.key === 'ArrowUp' || (event.ctrlKey && event.key.toLowerCase() === 'p')) {
      event.preventDefault()
      setSelectedIndex((index) => Math.max(index - 1, 0))
      return
    }
    if (event.key === 'Enter') {
      event.preventDefault()
      const item = flatItems[selectedIndex]
      if (item) runItem(item)
    }
  }

  const highlightQuery = parsed.scope === 'goto' || parsed.commandsOnly ? parsed.token || parsed.query : parsed.query
  let flatOffset = 0

  return (
    <Box
      sx={{
        width: '100%',
        minWidth: 0,
        flex: '1 1 0%',
      }}
    >
      <CommandPaletteTrigger
        ref={setTriggerNode}
        isMobile={isMobile}
        highlighted={demoActive}
        onOpen={() => openPalette()}
      />
      <CommandPaletteOnboarding
        anchorEl={demoAnchor}
        isAdmin={isAdmin}
        isMobile={isMobile}
        onOpenPalette={startDemoPalette}
        onSetQuery={setPaletteQuery}
        onClosePalette={endDemoPalette}
        onVisibilityChange={visible => {
          setDemoActive(visible)
          onDemoVisibilityChange?.(visible)
        }}
      />
      <Dialog
        open={open}
        onClose={() => {
          if (demoActive) return
          close()
        }}
        disableEscapeKeyDown={demoActive}
        fullWidth
        fullScreen={isMobile}
        maxWidth='sm'
        PaperProps={{
          sx: {
            height: isMobile ? '100dvh' : 'min(640px, 80vh)',
            maxHeight: isMobile ? '100dvh' : '80vh',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            m: isMobile ? 0 : 1,
            overscrollBehavior: 'contain',
            '@media (prefers-reduced-motion: reduce)': {
              transition: 'none',
            },
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            pt: 2,
            pb: 1,
            flexShrink: 0,
          }}
        >
          <Box ref={setDialogFieldNode} sx={{ flex: 1, minWidth: 0 }}>
          <TextField
            inputRef={inputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={onInputKeyDown}
            placeholder='Search the portal or type / for commands'
            fullWidth
            autoFocus={!demoActive}
            size='small'
            sx={{
              '& .MuiOutlinedInput-root': {
                minHeight: 40,
                maxHeight: 40,
              },
              '& .MuiInputBase-input': {
                minHeight: 0,
                py: 0,
                height: 40,
                boxSizing: 'border-box',
              },
            }}
            InputProps={{
              startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} aria-hidden />,
              endAdornment: isMobile ? (
                <IconButton aria-label='Close search' onClick={close} edge='end' size='small'>
                  <Close />
                </IconButton>
              ) : undefined,
            }}
          />
          </Box>
        </Box>
        <DialogContent sx={{ flex: 1, overflow: 'auto', pt: 0, px: 1, overscrollBehavior: 'contain' }}>
          {entity.isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={22} />
            </Box>
          )}
          {flatItems.length === 0 && !entity.isLoading && (
            <Typography variant='body2' color='text.secondary' sx={{ px: 2, py: 3 }}>
              {parsed.query.length > 0 && parsed.query.length < 2 && !parsed.commandsOnly
                ? 'Type at least 2 characters to search records'
                : 'No results'}
            </Typography>
          )}
          {grouped.map((section) => {
            const sectionStart = flatOffset
            flatOffset += section.items.length
            return (
              <Box key={section.group} sx={{ mb: 1 }}>
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{ px: 2, py: 0.5, display: 'block', fontWeight: 600 }}
                >
                  {PALETTE_GROUP_LABEL[section.group]}
                </Typography>
                <List disablePadding>
                  {section.items.map((item, index) => {
                    const flatIndex = sectionStart + index
                    const selected = flatIndex === selectedIndex
                    return (
                      <ListItemButton
                        key={item.id}
                        selected={selected}
                        onMouseEnter={() => setSelectedIndex(flatIndex)}
                        onClick={() => runItem(item)}
                        sx={{ py: 1, alignItems: 'flex-start' }}
                      >
                        <ListItemIcon sx={{ minWidth: 36, mt: 0.25 }}>
                          {groupIcon(item.group)}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                              <Typography variant='body2' fontWeight='medium' noWrap>
                                <HighlightText text={item.title} query={highlightQuery} />
                              </Typography>
                              {item.slash && (
                                <Typography variant='caption' color='text.secondary'>
                                  {item.slash}
                                </Typography>
                              )}
                            </Box>
                          }
                          secondary={
                            item.subtitle ? (
                              <Typography variant='caption' color='text.secondary' noWrap>
                                {item.subtitle}
                              </Typography>
                            ) : null
                          }
                        />
                        {item.chip && (
                          <Chip
                            label={item.chip}
                            size='small'
                            color={item.chipColor || 'default'}
                            sx={{ ml: 1, flexShrink: 0 }}
                          />
                        )}
                      </ListItemButton>
                    )
                  })}
                </List>
              </Box>
            )
          })}
        </DialogContent>
        <Box
          sx={{
            px: 2,
            py: 1,
            borderTop: 1,
            borderColor: 'divider',
            display: { xs: 'none', sm: 'flex' },
            gap: 2,
            color: 'text.secondary',
          }}
        >
          <Typography variant='caption'>↑↓ to move</Typography>
          <Typography variant='caption'>Enter to open</Typography>
          <Typography variant='caption'>Esc to close</Typography>
          <Typography variant='caption'>/ commands</Typography>
        </Box>
      </Dialog>
    </Box>
  )
}
